import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useTheme } from '@mui/material';
import { Button, Link } from '@rumblefish/ui/Rumblefish23Theme';
import { useState } from 'react';
import type { ourServicesData } from './OurServices.data';
import {
  StyledExpandButtonContainer,
  StyledSeeMoreButtonContainer,
  StyledServiceContentStack,
  StyledServiceDescription,
  StyledServiceIconBackground,
  StyledServiceIconWrapper,
  StyledServiceItemClickable,
  StyledServiceItemContainer,
  StyledServiceTitle,
  StyledUnderlinedText,
} from './styles';

export const OurServicesItem = ({
  service,
}: {
  service: (typeof ourServicesData)['categories'][number]['services'][number];
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isHover, setIsHover] = useState<boolean>(false);
  const Icon = service.icon;

  return (
    <StyledServiceItemContainer>
      <StyledServiceItemClickable
        direction="row"
        spacing={{ xs: 1, sm: 2 }}
        onClick={() => setIsOpen(!isOpen)}>
        <StyledServiceIconWrapper>
          <StyledServiceIconBackground>
            <Icon
              transitionduration={0.3}
              shouldbegradient={isHover}
              solidColor
              iconType="normal"
              sx={{
                color: theme.palette.invertedContent?.PRIMARY,
                transition: 'color .2s linear',
                '& .SvgFilledPath': {
                  transition: 'all .2s linear',
                },
                width: {
                  xs: '24px',
                  sm: '34px',
                },
                height: {
                  xs: '24px',
                  sm: '34px',
                },
                ...(isHover && {
                  '& .SvgFilledPath': {
                    fill: theme.palette.raspberryColors?.PLUS_1,
                  },
                  color: theme.palette.raspberryColors?.PLUS_1,
                }),
              }}
            />
          </StyledServiceIconBackground>
        </StyledServiceIconWrapper>

        <StyledServiceContentStack>
          <Link
            to={service.linkTo}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}>
            <StyledServiceTitle variant="strong_400">
              <StyledUnderlinedText>{service.name}</StyledUnderlinedText>
            </StyledServiceTitle>
          </Link>

          <StyledServiceDescription
            variant="regular_300"
            sx={{
              WebkitLineClamp: isOpen ? 'unset' : 2,
              transition: 'max-height 0.5s ease-in-out',
              maxHeight: isOpen ? '2500px' : '4em',
              overflow: 'hidden',
            }}>
            {service.description}
          </StyledServiceDescription>
        </StyledServiceContentStack>

        <StyledExpandButtonContainer isOpen={isOpen}>
          <AddRoundedIcon
            sx={{
              fontSize: {
                xs: '20px',
                sm: '28px',
              },
              transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
              transition: 'transform 0.1s ease-in-out',
              color: isOpen
                ? theme.palette.greyBackgroundColor?.INVERTED
                : theme.palette.greyBackgroundColor?.SURFACE,
            }}
          />
        </StyledExpandButtonContainer>
      </StyledServiceItemClickable>

      {isOpen && (
        <StyledSeeMoreButtonContainer>
          <Button link={service.linkTo} variant="link">
            See more
          </Button>
        </StyledSeeMoreButtonContainer>
      )}
    </StyledServiceItemContainer>
  );
};
